import { useMemo, useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import {
  PerformanceProcessPersonalResultsEvaluationsDocument,
  PerformanceReviewEvaluationSiblingReviewersDocument,
  PerformanceReviewEvaluationsDocument,
  PerformanceReviewOverviewPendingEvaluationsTaskDocument,
  type PerformanceReviewerStrategiesStrategyEnum,
  ReplaceReviewerPerformanceReviewEvaluationDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

interface Props {
  onClose: () => void
  evaluationId: number
  evaluationType: PerformanceReviewerStrategiesStrategyEnum
  excludedAccessIds?: Set<number>
}

export const ReplaceReviewerModal = ({
  onClose,
  evaluationId,
  evaluationType,
  excludedAccessIds: providedExcludedAccessIds,
}: Props) => {
  const [selectedAccessId, setSelectedAccessId] = useState<number | undefined>(undefined)

  const { data, loading } = useQuery(PerformanceReviewEvaluationSiblingReviewersDocument, {
    variables: { evaluationId, evaluationType },
    skip: providedExcludedAccessIds !== undefined,
  })

  const excludedAccessIds = useMemo(() => {
    if (providedExcludedAccessIds) {
      return providedExcludedAccessIds
    }

    const evaluations = data?.performance?.reviewEvaluation?.reviewProcessTarget.evaluations ?? []
    const reviewerIds = evaluations
      .map((e) => e.reviewerAccess?.id)
      .filter((id): id is number => id !== undefined)
    return reviewerIds
  }, [data, providedExcludedAccessIds])

  const [replaceReviewerEmployee, { loading: replacing }] = useMutation(
    ReplaceReviewerPerformanceReviewEvaluationDocument,
    {
      extractorFn: (result) =>
        result.performanceMutations.replaceReviewerPerformanceReviewEvaluation,
      refetchQueries: [
        PerformanceReviewEvaluationsDocument,
        PerformanceReviewOverviewPendingEvaluationsTaskDocument,
        PerformanceProcessPersonalResultsEvaluationsDocument,
      ],
    }
  )

  const handleReviewerChange = async () => {
    if (!selectedAccessId) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
      return
    }

    const response = await replaceReviewerEmployee({
      variables: {
        newReviewerAccessId: String(selectedAccessId),
        evaluationId: String(evaluationId),
      },
    })

    const result = handleResult(response, (errors) => {
      if (errors.messages) {
        app.setFlash({
          type: 'wrong',
          message: errors.messages,
        })
      }
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    onClose()
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.t('performance_process.reviews.evaluations.replace_reviewer')}
      description={i18n.t('performance_process.reviews.evaluations.replace_reviewer_description')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('common.save'),
        onClick: handleReviewerChange,
        disabled: !selectedAccessId || replacing || loading,
        loading: replacing,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {loading ? (
        <Loading />
      ) : (
        <EmployeeSelectorV2
          value={selectedAccessId}
          onChange={setSelectedAccessId}
          useAccessId
          mandatory
          forcedFilters={{
            excludeIds: Array.from(excludedAccessIds),
            includeInactive: false,
          }}
          messages={{
            searchEmptyMessage: i18n.t(
              'performance_process.reviews.basic_information_section.owners.empty_results'
            ),
          }}
        />
      )}
    </F0Dialog>
  )
}
